import React, { Component } from "react";
import PropTypes from "prop-types";
import style from "./style.module.css";
import PlayButtonIcon from "views/components/icons/PlayButtonIcon";
import PauseButtonIcon from "views/components/icons/PauseButtonIcon";
import MuteIcon from "views/components/icons/MuteIcon";
import UnmuteIcon from "views/components/icons/UnmuteIcon";

class VideoPlayer extends Component {
  static defaultProps = {
    autoPlay: false,
    muted: true,
    loop: true,
    controls: true,
    showPlayPause: true,
    showMute: true,
    showProgress: true,
    objectFit: "contain"
  };

  constructor(props) {
    super(props);

    this.videoRef = React.createRef();
    this.progressRef = React.createRef();

    this.state = {
      isPlaying: false,
      isMuted: this.props.muted,
      currentTime: 0,
      duration: 0,
      isLoaded: false,
      isDragging: false,
      videoWidth: 0,
      videoHeight: 0,
      aspectRatio: null
    };

    this.handlePlayPause = this.handlePlayPause.bind(this);
    this.handleMuteToggle = this.handleMuteToggle.bind(this);
    this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
    this.handleLoadedMetadata = this.handleLoadedMetadata.bind(this);
    this.handleProgressClick = this.handleProgressClick.bind(this);
    this.handleProgressMouseDown = this.handleProgressMouseDown.bind(this);
    this.handleProgressMouseMove = this.handleProgressMouseMove.bind(this);
    this.handleProgressMouseUp = this.handleProgressMouseUp.bind(this);
    this.handleVideoClick = this.handleVideoClick.bind(this);
  }

  componentDidMount() {
    const video = this.videoRef.current;
    if (video) {
      video.addEventListener("timeupdate", this.handleTimeUpdate);
      video.addEventListener("loadedmetadata", this.handleLoadedMetadata);

      // Set initial muted state
      video.muted = this.state.isMuted;

      if (this.props.autoPlay) {
        this.play();
      }
    }

    // Add global mouse event listeners for scrubbing
    document.addEventListener("mousemove", this.handleProgressMouseMove);
    document.addEventListener("mouseup", this.handleProgressMouseUp);
  }

  componentWillUnmount() {
    const video = this.videoRef.current;
    if (video) {
      video.removeEventListener("timeupdate", this.handleTimeUpdate);
      video.removeEventListener("loadedmetadata", this.handleLoadedMetadata);
    }

    document.removeEventListener("mousemove", this.handleProgressMouseMove);
    document.removeEventListener("mouseup", this.handleProgressMouseUp);
  }

  play() {
    const video = this.videoRef.current;
    if (video) {
      video
        .play()
        .then(() => {
          this.setState({ isPlaying: true });
        })
        .catch(error => {
          console.warn("Video play failed:", error);
        });
    }
  }

  pause() {
    const video = this.videoRef.current;
    if (video) {
      video.pause();
      this.setState({ isPlaying: false });
    }
  }

  handlePlayPause() {
    if (this.state.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  handleMuteToggle() {
    const video = this.videoRef.current;
    const newMutedState = !this.state.isMuted;

    if (video) {
      video.muted = newMutedState;
    }

    this.setState({ isMuted: newMutedState });
  }

  handleTimeUpdate() {
    const video = this.videoRef.current;
    if (video && !this.state.isDragging) {
      this.setState({
        currentTime: video.currentTime
      });
    }
  }

  handleLoadedMetadata() {
    const video = this.videoRef.current;
    if (video) {
      const aspectRatio = video.videoWidth / video.videoHeight;
      this.setState({
        duration: video.duration,
        isLoaded: true,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        aspectRatio: aspectRatio
      });

      if (this.props.onVideoLoaded) {
        this.props.onVideoLoaded();
      }
    }
  }

  handleProgressClick(event) {
    if (!this.state.isLoaded) return;

    const rect = this.progressRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * this.state.duration;

    this.seekTo(newTime);
  }

  handleProgressMouseDown(event) {
    event.preventDefault();
    this.setState({ isDragging: true });
    this.handleProgressClick(event);
  }

  handleProgressMouseMove(event) {
    if (!this.state.isDragging || !this.state.isLoaded) return;

    const rect = this.progressRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clickX = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * this.state.duration;

    this.seekTo(newTime);
  }

  handleProgressMouseUp() {
    this.setState({ isDragging: false });
  }

  handleVideoClick() {
    if (this.props.showPlayPause) {
      this.handlePlayPause();
    }
  }

  seekTo(time) {
    const video = this.videoRef.current;
    if (video) {
      video.currentTime = Math.max(0, Math.min(time, this.state.duration));
      this.setState({ currentTime: video.currentTime });
    }
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  render() {
    const {
      src,
      fallbackVideoSrc,
      className,
      style: inlineStyle,
      controls,
      showPlayPause,
      showMute,
      showProgress,
      borderRadius,
      ...videoProps
    } = this.props;

    const { isPlaying, isMuted, currentTime, duration, isLoaded, aspectRatio } =
      this.state;

    const progressPercentage =
      duration > 0 ? (currentTime / duration) * 100 : 0;

    // Calculate dynamic container dimensions
    let containerStyle = { ...inlineStyle };
    if (aspectRatio) {
      const maxWidth = 280;
      const maxHeight = 300;
      let width = maxWidth;
      let height = width / aspectRatio;

      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }

      containerStyle = {
        ...containerStyle,
        width: `${width}px`,
        height: `${height}px`
      };
    }

    const isCompact =
      containerStyle.width && parseInt(containerStyle.width, 10) < 200;

    return (
      <div
        className={`${style.videoPlayerContainer} ${className || ""}`}
        style={containerStyle}
      >
        <div className={style.videoWrapper} style={{ borderRadius }}>
          <video
            ref={this.videoRef}
            src={src || fallbackVideoSrc}
            className={style.video}
            onClick={this.handleVideoClick}
            loop={this.props.loop}
            muted={isMuted}
            style={{ borderRadius, objectFit: this.props.objectFit }}
            {...videoProps}
          />

          {controls && (
            <div
              className={`${style.controls} ${isCompact ? style.compact : ""}`}
            >
              {showProgress && (
                <div className={style.progressContainer}>
                  <div
                    ref={this.progressRef}
                    className={style.progressBar}
                    onClick={this.handleProgressClick}
                    onMouseDown={this.handleProgressMouseDown}
                  >
                    <div
                      className={style.progressFill}
                      style={{ width: `${progressPercentage}%` }}
                    />
                    <div
                      className={style.progressHandle}
                      style={{ left: `${progressPercentage}%` }}
                    />
                  </div>
                </div>
              )}
              <div className={style.bottomControls}>
                <div className={style.leftControls}>
                  {showPlayPause && (
                    <button
                      className={style.controlButton}
                      onClick={this.handlePlayPause}
                      title={isPlaying ? "Pause" : "Play"}
                    >
                      {isPlaying ? (
                        <PauseButtonIcon color="#FFFFFF" size="20" />
                      ) : (
                        <PlayButtonIcon color="#FFFFFF" size="20" />
                      )}
                    </button>
                  )}

                  {showMute && (
                    <button
                      className={`${style.controlButton} ${
                        isMuted ? style.muted : ""
                      }`}
                      onClick={this.handleMuteToggle}
                      title={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted ? (
                        <MuteIcon size="18" color="#FFFFFF" />
                      ) : (
                        <UnmuteIcon size="18" color="#FFFFFF" />
                      )}
                    </button>
                  )}
                </div>

                {showProgress && isLoaded && (
                  <div className={style.timeDisplay}>
                    {this.formatTime(currentTime)}
                    {!isCompact && ` / ${this.formatTime(duration)}`}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  fallbackVideoSrc: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  autoPlay: PropTypes.bool,
  muted: PropTypes.bool,
  loop: PropTypes.bool,
  controls: PropTypes.bool,
  showPlayPause: PropTypes.bool,
  showMute: PropTypes.bool,
  showProgress: PropTypes.bool,
  borderRadius: PropTypes.string,
  objectFit: PropTypes.oneOf([
    "contain",
    "cover",
    "fill",
    "none",
    "scale-down"
  ]),
  onVideoLoaded: PropTypes.func
};

export default VideoPlayer;
